import * as React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { useKbTree, type KbTree } from '@/lib/api/vendor-knowledge';
import { cn } from '@/lib/utils/cn';
import { Empty, Linkish, Page } from './kb-bits';
import { KbListPane } from './list-pane';
import { KbHome } from './home';
import { VendorPage } from './vendor-page';
import { PartnerPage } from './partner-page';
import { NewVendorPage } from './new-vendor';
import { KbSettingsPage } from './settings/settings-page';
import { VendorListPage, VendorListsPage } from './lists/vendor-lists';

/**
 * Firm settings → Vendor knowledge base (T-0206, T-0207; E13-F01, F02,
 * F06). One screen: the list of vendors and partners on the left, the
 * selected one on the right, read top to bottom. No overlays and no tabs:
 * details open in place. Prototype: `docs/vendor-knowledge/prototype/`
 * (`#kb`, `#kb/<vendor>`, `#kb/<partner>`, `#kb/new`, `#kb/settings`).
 *
 * The route owns the selection (`?vendor=`, `?partner=`, `?view=`,
 * `?list=`), so a page can be linked to and Back works. Vendor lists
 * (T-0267, `?view=lists`) are the curated lists engagements pick vendors
 * from.
 */

export type KbView =
  | { kind: 'home' }
  | { kind: 'vendor'; id: string }
  | { kind: 'partner'; id: string }
  | { kind: 'new' }
  | { kind: 'settings' }
  | { kind: 'lists' }
  | { kind: 'list'; id: string };

export interface KbContextValue {
  tree: KbTree;
  canCurate: boolean;
  view: KbView;
  navigate: (view: KbView) => void;
  /** The element the page scrolls in (virtual lists measure against it). */
  scrollElement: () => HTMLElement | null;
  /** Bring an element of the page into view (a section, a review group). */
  scrollTo: (id: string) => void;
  /** Open another page of the app (Firm settings → AI costs). */
  openPath: (path: string) => void;
}

const KbContext = React.createContext<KbContextValue | null>(null);

export function useKb(): KbContextValue {
  const ctx = React.useContext(KbContext);
  if (!ctx) throw new Error('useKb must be used inside VendorKnowledgeScreen');
  return ctx;
}

export function VendorKnowledgeScreen({
  view,
  onNavigate,
  onOpenPath,
}: {
  view: KbView;
  onNavigate: (view: KbView) => void;
  /** The router's navigation for links out of the screen; a plain page load without it. */
  onOpenPath?: (path: string) => void;
}) {
  const tree = useKbTree();
  const outerRef = React.useRef<HTMLDivElement>(null);
  const pageRef = React.useRef<HTMLDivElement>(null);
  const pendingScroll = React.useRef<string | null>(null);

  const scrollElement = React.useCallback((): HTMLElement | null => {
    const page = pageRef.current;
    // Wide screens scroll the page pane; narrow ones scroll the whole column.
    if (page && page.scrollHeight > page.clientHeight + 1) return page;
    if (page && getComputedStyle(page).overflowY === 'auto') return page;
    return outerRef.current;
  }, []);

  const scrollTo = React.useCallback(
    (id: string) => {
      const el = document.getElementById(id);
      const pane = scrollElement();
      if (!el || !pane) {
        pendingScroll.current = id;
        return;
      }
      pendingScroll.current = null;
      pane.scrollTop += el.getBoundingClientRect().top - pane.getBoundingClientRect().top - 16;
    },
    [scrollElement],
  );

  const navigate = React.useCallback(
    (next: KbView) => {
      onNavigate(next);
    },
    [onNavigate],
  );

  // A new selection starts at the top of its page.
  const viewKey =
    view.kind === 'vendor' || view.kind === 'partner' || view.kind === 'list'
      ? `${view.kind}:${view.id}`
      : view.kind;
  React.useEffect(() => {
    const pane = pageRef.current;
    if (pane) pane.scrollTop = 0;
    if (outerRef.current) outerRef.current.scrollTop = 0;
  }, [viewKey]);

  // A scroll asked for before its target rendered (a guide step opening a vendor).
  React.useEffect(() => {
    if (!pendingScroll.current) return;
    const id = pendingScroll.current;
    const t = window.setTimeout(() => scrollTo(id), 50);
    return () => window.clearTimeout(t);
  });

  if (tree.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (tree.isError) {
    return (
      <div className="p-6">
        <ErrorMessage error={tree.error} />
      </div>
    );
  }

  const ctx: KbContextValue = {
    tree: tree.data,
    canCurate: tree.data.can_curate,
    view,
    navigate,
    scrollElement,
    scrollTo,
    openPath: (path) => (onOpenPath ? onOpenPath(path) : window.location.assign(path)),
  };
  const selected = view.kind !== 'home';

  return (
    <KbContext.Provider value={ctx}>
      <div
        ref={outerRef}
        className="flex h-full min-h-0 flex-col overflow-y-auto md:flex-row md:overflow-hidden"
      >
        <KbListPane
          className={cn(
            'w-full border-b md:w-[300px] md:border-b-0 md:border-r',
            selected ? 'hidden md:flex' : 'flex max-h-[60vh] md:max-h-none',
          )}
        />
        <div
          ref={pageRef}
          className="min-w-0 flex-1 md:min-h-0 md:overflow-y-auto"
          data-testid="kb-page"
        >
          {selected ? (
            <div className="px-4 pt-3 md:hidden">
              <Linkish onClick={() => navigate({ kind: 'home' })}>
                <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> All vendors and partners
              </Linkish>
            </div>
          ) : null}
          <RightSide view={view} tree={tree.data} />
        </div>
      </div>
    </KbContext.Provider>
  );
}

function RightSide({ view, tree }: { view: KbView; tree: KbTree }) {
  switch (view.kind) {
    case 'vendor': {
      const vendor = tree.vendors.find((v) => v.id === view.id);
      return vendor ? <VendorPage key={vendor.id} vendorId={vendor.id} /> : <Missing />;
    }
    case 'partner': {
      const partner = tree.partners.find((p) => p.id === view.id);
      return partner ? <PartnerPage key={partner.id} partnerId={partner.id} /> : <Missing />;
    }
    case 'new':
      return tree.can_curate ? <NewVendorPage /> : <Missing />;
    case 'settings':
      return tree.can_curate ? <KbSettingsPage /> : <Missing />;
    case 'lists':
      return <VendorListsPage />;
    case 'list':
      return <VendorListPage key={view.id} listId={view.id} />;
    default:
      return <KbHome />;
  }
}

function Missing() {
  const { navigate } = useKb();
  return (
    <Page>
      <Empty>
        This page isn’t in the knowledge base.{' '}
        <Linkish onClick={() => navigate({ kind: 'home' })}>Back to the knowledge base</Linkish>
      </Empty>
    </Page>
  );
}
